import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useEffect, useState } from "react";
import CustomInput from "../../inputs/customInput";
import CityDropdown from "../../dropdowns/cityDropdown";
import { useTheme } from "@emotion/react";
import CityPriceService from "../../../services/cityPriceService";
import BranchDropdown from "../../dropdowns/branchDropdown";

const UpdateCityPriceBox = ({
    handleReload,
    setPopupMsg,
    setPopupType,
    setShowPopup,
    cityPrice }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [formValues, setFormValues] = useState({
        transportationPrice: cityPrice.transportationPrice,
        cityId: cityPrice.city && cityPrice.city.id,
        branchId: cityPrice.branch && cityPrice.branch.id,
    });

    const [selectedCity, setSelectedCity] = useState(cityPrice.city);
    const [selectedBranch, setSelectedBranch] = useState(cityPrice.branch);
    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/,/g, "");

        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: numericValue,
        }));
    };

    useEffect(() => {
        if (cityPrice) {
            setFormValues({
                transportationPrice: cityPrice.transportationPrice || 0,
                cityId: cityPrice.city && cityPrice.city.id,
                branchId: cityPrice.branch && cityPrice.branch.id
            });
            setSelectedCity(cityPrice.city);
            setSelectedBranch(cityPrice.branch);
            setUpdateErrors({});
        }
    }, [cityPrice]);

    const validateForm = () => {
        const newUpdateErrors = {};

        if (isNaN(formValues.transportationPrice)) {
            newUpdateErrors.transportationPrice = t('transportationPriceNumber');
        }

        setUpdateErrors(newUpdateErrors);

        return Object.keys(newUpdateErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await CityPriceService.update({
                    id: cityPrice.id,
                    ...formValues
                });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('update');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({ transportationPrice: 0 });
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                p: isTablet ? 2 : 4,
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                    {t('updateCityPrice')}
                </Typography>
            </Box>
            <CityDropdown
                helperText={updateErrors.city}
                error={!!updateErrors.city}
                setFormValues={setFormValues}
                isFilter={false}
                selectedCity={selectedCity || null}
                setSelectedCity={setSelectedCity}
            />

            <BranchDropdown
                helperText={updateErrors.branch}
                error={!!updateErrors.branch}
                setFormValues={setFormValues}
                isFilter={false}
                selectedBranch={selectedBranch || null}
                setSelectedBranch={setSelectedBranch}
            />

            <CustomInput
                label={t('transportationPrice')}
                placeholder={t('transportationPriceHere')}
                name="transportationPrice"
                type='text'
                value={formValues.transportationPrice ? Number(formValues.transportationPrice).toLocaleString() : 0}
                error={!!updateErrors.transportationPrice}
                helperText={updateErrors.transportationPrice}
                onChange={handleNumberChange}
            />

            {updateErrors.general && (
                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                    {updateErrors.general}
                </Typography>
            )}

            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                mt: updateErrors.general ? 2 : 4
            }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                >
                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <p>{t('update')}</p>
                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                        </Box>
                    }
                </Button>
            </Box>
        </Box>
    );
}

export default UpdateCityPriceBox;
